import type {
  AiRenderJob,
  AiRenderUsage,
  CreateAiRenderInput,
  FloorPlan,
  CreateFloorPlanInput,
  UpdateFloorPlanInput,
  AssignGuestsToTableInput,
  TableAssignment,
  Room,
  CreateRoomInput,
  UpdateRoomInput,
  CreateRoomEntryInput,
  RoomEntry,
  FloorPlanObject,
  CreateFloorPlanObjectInput,
  UpdateFloorPlanObjectInput,
  BulkUpsertObjectsInput,
  DuplicateFloorPlanInput,
  SaveFloorPlanObjectsResult,
  SeatingAutoAssignMode,
  SeatingStrategy,
  ChildrenSeating,
  SeatKind,
  FloorPlanVersionSummary,
  FloorPlanSaveConflict,
} from '@peles/shared';
import { apiClient } from './client';

// ─── Floor Plans ────────────────────────────────────────────────────────────

export async function getFloorPlans(filters?: {
  eventId?: string;
  venueId?: string;
  isTemplate?: boolean;
}): Promise<FloorPlan[]> {
  const params: Record<string, string> = {};
  if (filters?.eventId) params.eventId = filters.eventId;
  if (filters?.venueId) params.venueId = filters.venueId;
  if (filters?.isTemplate) params.isTemplate = 'true';
  const { data } = await apiClient.get<FloorPlan[]>('/floor-plans', {
    params,
  });
  return data;
}

export async function getFloorPlan(id: string): Promise<FloorPlan> {
  const { data } = await apiClient.get<FloorPlan>(`/floor-plans/${id}`);
  return data;
}

export async function createFloorPlan(input: CreateFloorPlanInput): Promise<FloorPlan> {
  const { data } = await apiClient.post<FloorPlan>('/floor-plans', input);
  return data;
}

export async function updateFloorPlan(
  id: string,
  updates: UpdateFloorPlanInput,
): Promise<FloorPlan> {
  const { data } = await apiClient.patch<FloorPlan>(`/floor-plans/${id}`, updates);
  return data;
}

export async function deleteFloorPlan(id: string): Promise<void> {
  await apiClient.delete(`/floor-plans/${id}`);
}

export async function assignGuestsToTable(input: AssignGuestsToTableInput): Promise<void> {
  await apiClient.post('/floor-plans/assign-guests', input);
}

export async function getTableAssignments(
  eventId: string,
  floorPlanId?: string,
): Promise<Record<number, TableAssignment['guests']>> {
  const { data } = await apiClient.get<Record<number, TableAssignment['guests']>>(
    '/floor-plans/assignments',
    { params: floorPlanId ? { eventId, floorPlanId } : { eventId } },
  );
  return data;
}

export async function cloneAsTemplate(id: string, templateName?: string): Promise<FloorPlan> {
  const { data } = await apiClient.post<FloorPlan>(`/floor-plans/${id}/clone-template`, {
    templateName,
  });
  return data;
}

export async function applyTemplate(
  templateId: string,
  eventId: string,
  name?: string,
): Promise<FloorPlan> {
  const { data } = await apiClient.post<FloorPlan>(`/floor-plans/templates/${templateId}/apply`, {
    eventId,
    name,
  });
  return data;
}

// ─── Rooms ──────────────────────────────────────────────────────────────────

export async function getRooms(venueId?: string): Promise<Room[]> {
  const params: Record<string, string> = {};
  if (venueId) params.venueId = venueId;
  const { data } = await apiClient.get<Room[]>('/rooms', { params });
  return data;
}

export async function createRoom(input: CreateRoomInput): Promise<Room> {
  const { data } = await apiClient.post<Room>('/rooms', input);
  return data;
}

export async function updateRoom(id: string, updates: UpdateRoomInput): Promise<Room> {
  const { data } = await apiClient.patch<Room>(`/rooms/${id}`, updates);
  return data;
}

export async function deleteRoom(id: string): Promise<void> {
  await apiClient.delete(`/rooms/${id}`);
}

export async function createRoomEntry(
  roomId: string,
  input: CreateRoomEntryInput,
): Promise<RoomEntry> {
  const { data } = await apiClient.post<RoomEntry>(`/rooms/${roomId}/entries`, input);
  return data;
}

export async function deleteRoomEntry(roomId: string, entryId: string): Promise<void> {
  await apiClient.delete(`/rooms/${roomId}/entries/${entryId}`);
}

// ─── Floor Plan Objects ─────────────────────────────────────────────────────

export async function createFloorPlanObject(
  floorPlanId: string,
  input: CreateFloorPlanObjectInput,
): Promise<FloorPlanObject> {
  const { data } = await apiClient.post<FloorPlanObject>(
    `/floor-plans/${floorPlanId}/objects`,
    input,
  );
  return data;
}

export async function updateFloorPlanObject(
  floorPlanId: string,
  objId: string,
  updates: UpdateFloorPlanObjectInput,
): Promise<FloorPlanObject> {
  const { data } = await apiClient.patch<FloorPlanObject>(
    `/floor-plans/${floorPlanId}/objects/${objId}`,
    updates,
  );
  return data;
}

export async function deleteFloorPlanObject(floorPlanId: string, objId: string): Promise<void> {
  await apiClient.delete(`/floor-plans/${floorPlanId}/objects/${objId}`);
}

/**
 * Save the whole canvas. The server applies it as a diff and answers with the
 * stored objects and the plan's new version; pass `expectedVersion` and a
 * save made from an older copy is refused with 409 (see `saveConflictOf`).
 */
export async function bulkUpsertObjects(
  floorPlanId: string,
  input: BulkUpsertObjectsInput,
): Promise<SaveFloorPlanObjectsResult> {
  const { data } = await apiClient.put<SaveFloorPlanObjectsResult>(
    `/floor-plans/${floorPlanId}/objects`,
    input,
  );
  return data;
}

/** The conflict a stale save was refused with, or null for any other error. */
export function saveConflictOf(err: unknown): FloorPlanSaveConflict | null {
  const response = (err as { response?: { status?: number; data?: { error?: unknown } } })
    ?.response;
  if (response?.status !== 409) return null;
  const body = response.data?.error as FloorPlanSaveConflict | undefined;
  return body?.code === 'FLOOR_PLAN_VERSION_CONFLICT' ? body : null;
}

// ─── Versions ───────────────────────────────────────────────────────────────

export async function getFloorPlanVersions(
  floorPlanId: string,
): Promise<FloorPlanVersionSummary[]> {
  const { data } = await apiClient.get<FloorPlanVersionSummary[]>(
    `/floor-plans/${floorPlanId}/versions`,
  );
  return data;
}

export async function restoreFloorPlanVersion(
  floorPlanId: string,
  versionId: string,
  expectedVersion?: number,
): Promise<SaveFloorPlanObjectsResult> {
  const { data } = await apiClient.post<SaveFloorPlanObjectsResult>(
    `/floor-plans/${floorPlanId}/versions/${versionId}/restore`,
    { expectedVersion },
  );
  return data;
}

// ─── Duplicate ──────────────────────────────────────────────────────────────

export async function duplicateFloorPlan(
  id: string,
  input: DuplicateFloorPlanInput,
): Promise<FloorPlan> {
  const { data } = await apiClient.post<FloorPlan>(`/floor-plans/${id}/duplicate`, input);
  return data;
}

// ─── Share ───────────────────────────────────────────────────────────────────

export async function shareFloorPlan(id: string): Promise<{ shareToken: string; url: string }> {
  const { data } = await apiClient.post<{ shareToken: string; url: string }>(
    `/floor-plans/${id}/share`,
  );
  return data;
}

export async function getPublicFloorPlan(
  token: string,
): Promise<{ floorPlan: FloorPlan; guests: any[] }> {
  const { data } = await apiClient.get<{ floorPlan: FloorPlan; guests: any[] }>(
    `/public/floor-plans/${token}`,
  );
  return data;
}

// ─── Seat Assignment ─────────────────────────────────────────────────────────

export async function assignGuestToSeat(
  guestId: string,
  tableNumber: number,
  seatNumber: number,
  allowOverCapacity?: boolean,
  floorPlanId?: string,
): Promise<void> {
  await apiClient.post('/floor-plans/assign-seat', {
    guestId,
    tableNumber,
    seatNumber,
    ...(allowOverCapacity ? { allowOverCapacity } : {}),
    ...(floorPlanId ? { floorPlanId } : {}),
  });
}

// ─── Meal Counts ─────────────────────────────────────────────────────────────

export async function getTableCapacities(eventId: string): Promise<Record<number, number>> {
  const { data } = await apiClient.get<Record<number, number>>('/floor-plans/table-capacities', {
    params: { eventId },
  });
  return data;
}

export async function getTableMealCounts(
  eventId: string,
): Promise<Record<number, Record<string, number>>> {
  const { data } = await apiClient.get<Record<number, Record<string, number>>>(
    '/floor-plans/meal-counts',
    { params: { eventId } },
  );
  return data;
}

// ─── Auto-Assign Seats ────────────────────────────────────────────────────

export interface AutoAssignSeatsRequest {
  strategy: SeatingStrategy;
  respectKeepTogether?: boolean;
  /** `MECHITZA` only: where the children sit. */
  childrenWith?: ChildrenSeating;
  dryRun?: boolean;
  /** Seat only those not seated yet (the default), or start again for everyone. */
  mode?: SeatingAutoAssignMode;
  autoAddTables?: {
    seatsPerTable: number;
    tableType?: 'ROUND' | 'OVAL' | 'SQUARE' | 'RECTANGULAR' | 'HEAD';
  };
}

export interface AutoAssignSeatsResult {
  /** A party's place at one table — a party split across tables has one per table. */
  assignments: Array<{
    guestId: string;
    tableNumber: number;
    tableUid: string;
    seatNumber: number;
    seatCount: number;
    seats: number[];
    /** Who each seat is for, when the run seated by section. */
    kinds?: SeatKind[];
  }>;
  unassigned: Array<{ guestId: string; reason: string }>;
  shortfall: { partySeats: number; tablesNeeded: number };
  tablesAdded: Array<{ id: string; tableNumber: number; seats: number }>;
  /** The parties the run re-seats — with `assignments`, the arrangement proposed. */
  reseat: string[];
  applied: boolean;
}

export async function autoAssignSeats(
  floorPlanId: string,
  body: AutoAssignSeatsRequest,
): Promise<AutoAssignSeatsResult> {
  const { data } = await apiClient.post<AutoAssignSeatsResult>(
    `/floor-plans/${floorPlanId}/auto-assign`,
    body,
  );
  return data;
}

// ─── AI Seating ───────────────────────────────────────────────────────────

export interface AiSeatingResult {
  assignments: Array<{
    guestId: string;
    tableNumber: number;
    seatNumber: number;
    tableUid?: string;
  }>;
  unassigned: Array<{ guestId: string; reason: string }>;
  rationale: string;
  warnings: string[];
  /** The parties the arrangement re-seats (everyone offered to the model). */
  reseat: string[];
  applied: boolean;
}

export async function aiSuggestSeats(
  floorPlanId: string,
  dryRun: boolean,
  reviewedAssignments?: Array<{ guestId: string; tableNumber: number }>,
): Promise<AiSeatingResult> {
  const { data } = await apiClient.post<AiSeatingResult>(
    `/floor-plans/${floorPlanId}/ai-suggest-seats`,
    { dryRun, ...(reviewedAssignments ? { reviewedAssignments } : {}) },
  );
  return data;
}

// ─── AI renders ──────────────────────────────────────────────────────────────

export async function getFloorPlanRenders(
  floorPlanId: string,
): Promise<{ jobs: AiRenderJob[]; usage: AiRenderUsage }> {
  const { data } = await apiClient.get(`/floor-plans/${floorPlanId}/renders`);
  return data;
}

export async function getRenderUploadUrl(
  floorPlanId: string,
  fileName: string,
  mimeType: string,
): Promise<{ uploadUrl: string; s3Key: string }> {
  const { data } = await apiClient.post(`/floor-plans/${floorPlanId}/renders/upload-url`, {
    fileName,
    mimeType,
  });
  return data;
}

export async function createFloorPlanRender(
  floorPlanId: string,
  input: CreateAiRenderInput,
): Promise<AiRenderJob> {
  const { data } = await apiClient.post(`/floor-plans/${floorPlanId}/renders`, input);
  return data;
}
